import {
  ApiError,
  type AutonomousVehicle,
  AutonomousVehicleAuthorityService,
  type AutonomousVehicleContact,
  AutonomousVehicleContactType,
  type AutonomousVehicleRegistration,
  type CertifyAutonomousVehicleAuthorityRegistrationRequest,
  type CreateAutonomousVehicleAuthorityContactRequest,
  CreateAutonomousVehicleAuthorityRegistrationRequest,
  CreateAutonomousVehicleAuthorityRegistrationUsdotRequest,
  type CreateAutonomousVehicleRequest,
  type UpdateAutonomousVehicleAuthorityContactRequest,
  VinCheckStatus,
} from '@/@types/generated'
import { useBusinessEntity } from '@/hooks/business-entity'
import { useAuth } from '@/hooks/useAuth'
import { useError } from '@/hooks/useError'
import React, { useMemo } from 'react'

export const RegistrationNew = 'new'

export class VinConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'VinConflictError'
  }
}

export class InvalidMakeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidMakeError'
  }
}

type TSetPrimaryContact =
  | CreateAutonomousVehicleAuthorityContactRequest
  | UpdateAutonomousVehicleAuthorityContactRequest

interface AutonomousVehicleApplicationContextProps {
  readonly loading: boolean
  readonly registration: AutonomousVehicleRegistration | null
  readonly primaryContact: AutonomousVehicleContact | null
  readonly vehicles: AutonomousVehicle[]
  loadVehicles(): Promise<void>

  // Methods
  register(
    req: CreateAutonomousVehicleAuthorityRegistrationRequest
  ): Promise<AutonomousVehicleRegistration>
  registerFromUsdot(
    req: CreateAutonomousVehicleAuthorityRegistrationUsdotRequest
  ): Promise<AutonomousVehicleRegistration | null>
  setPrimaryContact(contact: TSetPrimaryContact): Promise<void>
  addVehicle(
    req: CreateAutonomousVehicleRequest,
    forceAdd?: boolean
  ): Promise<void>
  removeVehicle(vehicle: AutonomousVehicle): Promise<void>
  submit(
    req: CertifyAutonomousVehicleAuthorityRegistrationRequest
  ): Promise<void>
  approve: (restrictionsNote?: string) => Promise<void>
  reject: (restrictionsNote?: string) => Promise<void>
  reset: () => Promise<void>
}

const AutonomousVehicleApplicationContext =
  React.createContext<AutonomousVehicleApplicationContextProps>(undefined!)

export function AutonomousVehicleApplicationProvider({
  registrationId,
  children,
  onNotFound,
}: React.PropsWithChildren<{
  registrationId?: string
  onNotFound?: () => void
}>) {
  // Hooks
  const { showError } = useError()
  const { loadBusinessEntities } = useBusinessEntity()
  const { refreshAuth } = useAuth()

  // State
  const [loading, setLoading] = React.useState(0)
  const [registration, setRegistration] =
    React.useState<AutonomousVehicleRegistration | null>(null)
  const [contacts, setContacts] = React.useState<AutonomousVehicleContact[]>([])
  const [vehicles, setVehicles] = React.useState<AutonomousVehicle[]>([])

  // Register the business information, returning and setting the registration
  const register = React.useCallback(
    async (
      req: CreateAutonomousVehicleAuthorityRegistrationRequest
    ): Promise<AutonomousVehicleRegistration> => {
      // Registration already exists, do not allow re-registration
      if (registration)
        throw new Error(
          'Automated motor vehicle application is already registered'
        )

      try {
        setLoading((prev) => prev + 1)

        const resp = await AutonomousVehicleAuthorityService.create(req)
        setRegistration(resp.registration)

        // Refresh auth to get updated business entities
        await refreshAuth()

        return resp.registration
      } catch (error) {
        showError(
          error,
          'Failed to register automated motor vehicle application'
        )
        throw error
      } finally {
        setLoading((prev) => prev - 1)
      }
    },
    [registration, refreshAuth, showError]
  )

  // Register the business entity from USDOT, returning and setting the registration
  const registerFromUsdot = React.useCallback(
    async (req: CreateAutonomousVehicleAuthorityRegistrationUsdotRequest) => {
      if (registration)
        throw new Error(
          'Automated motor vehicle application is already registered'
        )

      try {
        setLoading((prev) => prev + 1)
        const { registration } =
          await AutonomousVehicleAuthorityService.createFromUsdot(req)

        // User already has access to this business
        if (registration) setRegistration(registration)

        // Refresh auth to get updated business entities
        await refreshAuth()

        return registration || null
      } finally {
        setLoading((prev) => prev - 1)
      }
    },
    [registration, refreshAuth]
  )

  // Load the registration for the current user from the API
  const getRegistration = React.useCallback(async () => {
    if (!registrationId || registrationId === RegistrationNew)
      return setRegistration(null)

    try {
      setLoading((prev) => prev + 1)
      const { registration } =
        await AutonomousVehicleAuthorityService.getRegistration(registrationId)
      setRegistration(registration)
    } catch (error) {
      // Catch 404, means no registration exists
      if (error instanceof ApiError && error.status === 404) {
        setRegistration(null)
        onNotFound?.()
        return
      }

      // Other errors
      showError(error, 'Failed to load automated motor vehicle application')
      throw error
    } finally {
      setLoading((prev) => prev - 1)
    }
  }, [onNotFound, registrationId, showError])

  // Load the contacts for the current registration
  const listContacts = React.useCallback(async () => {
    if (!registration?.id) return setContacts([])

    try {
      setLoading((prev) => prev + 1)
      const { contacts } = await AutonomousVehicleAuthorityService.listContacts(
        registration.id
      )
      setContacts(contacts || [])
    } catch (error) {
      // Other errors
      showError(
        error,
        'Failed to load automated motor vehicle application contacts'
      )
      throw error
    } finally {
      setLoading((prev) => prev - 1)
    }
  }, [registration?.id, showError])

  // Set the primary contact for the current registration
  // NOTE: This creates or updates the contact if it does not exist
  const primaryContact = React.useMemo(
    () =>
      contacts.find((c) => c.type === AutonomousVehicleContactType.PRIMARY) ||
      null,
    [contacts]
  )
  const setPrimaryContact = React.useCallback(
    async (contact: TSetPrimaryContact) => {
      if (!registration?.id)
        throw new Error(
          'Cannot set primary contact without an automated motor vehicle registration'
        )

      try {
        // Create
        if (!primaryContact)
          await AutonomousVehicleAuthorityService.createContact(
            registration.id,
            { type: AutonomousVehicleContactType.PRIMARY, ...contact }
          )
        // Update
        else
          await AutonomousVehicleAuthorityService.updateContact(
            registration.id,
            AutonomousVehicleContactType.PRIMARY,
            contact
          )

        // Refresh contacts
        await listContacts()
      } catch (error) {
        showError(
          error,
          'Failed to set primary contact for automated motor vehicle application'
        )
        throw error
      }
    },
    [registration?.id, primaryContact, listContacts, showError]
  )

  // Load vehicles for this registration
  const loadVehicles = React.useCallback(async () => {
    if (!registration?.id) return setVehicles([])

    try {
      setLoading((prev) => prev + 1)
      const vehicles = await AutonomousVehicleAuthorityService.listVehicles(
        registration.id
      )
      setVehicles(vehicles)
    } finally {
      setLoading((prev) => prev - 1)
    }
  }, [registration?.id])

  // Vehicle added, filtered by the above VehicleFields
  const addVehicle = React.useCallback(
    async (req: CreateAutonomousVehicleRequest, forceAdd?: boolean) => {
      if (!registration?.id)
        throw new Error('Cannot add vehicle without registration')

      if (forceAdd) {
        const vehicle = await AutonomousVehicleAuthorityService.createVehicle(
          registration.id,
          req
        )
        setVehicles((prev) => [...prev, vehicle])
        return
      }

      const result =
        await AutonomousVehicleAuthorityService.createVehicleWithVinCheck(
          registration.id,
          req
        ).catch((error) => {
          if (error instanceof ApiError && error.status === 500) {
            throw new InvalidMakeError(
              (error as any)?.body?.detail ?? 'Invalid vehicle make.'
            )
          }
          throw error
        })
      if (
        'status' in result &&
        result.status === VinCheckStatus.EXISTS_AT_ANOTHER_REGISTRATION
      ) {
        throw new VinConflictError(
          'Our records indicate that this vehicle already exists on another account. Please double check the VIN you entered. ' +
            'If you are confident in the VIN you have entered, please click "Yes, Add Vehicle" below.'
        )
      }
      setVehicles((prev) => [...prev, result as AutonomousVehicle])
    },
    [registration?.id]
  )

  // Vehicle removed
  const removeVehicle = React.useCallback(
    async ({ id }: AutonomousVehicle) => {
      if (!registration?.id)
        throw new Error('Cannot remove vehicle without registration')

      // Delete vehicle API
      await AutonomousVehicleAuthorityService.deleteVehicle(id)

      // Remove vehicle from list
      setVehicles((prev) => prev.filter((v) => v.id !== id))
    },
    [registration?.id]
  )

  // Certify and submit the application
  const submit = React.useCallback(
    async (req: CertifyAutonomousVehicleAuthorityRegistrationRequest) => {
      if (!registration?.id)
        throw new Error('Cannot submit application without registration')

      try {
        setLoading((prev) => prev + 1)
        // Certify the application
        await AutonomousVehicleAuthorityService.certify(registration.id, req)

        // Submit the application
        await AutonomousVehicleAuthorityService.submit(registration.id)

        // Reload registration to get updated status
        await getRegistration()

        // Refresh auth to get updated business entities
        await loadBusinessEntities(true)
      } catch (error) {
        showError(
          error,
          'Failed to submit automated motor vehicle application. Please try again.'
        )
        throw error
      } finally {
        setLoading((prev) => prev - 1)
      }
    },
    [registration?.id, getRegistration, loadBusinessEntities, showError]
  )

  // Approve the application, after submission and certification
  const approve = React.useCallback(
    async (restrictionsNote?: string) => {
      if (!registration?.id)
        throw new Error('Cannot approve application without registration')

      try {
        setLoading((prev) => prev + 1)
        // Approve the application
        await AutonomousVehicleAuthorityService.approve(
          registration.id,
          restrictionsNote ? { restrictionsNote } : {}
        )

        // Reload registration to get updated status
        await getRegistration()

        // Refresh auth to get updated business entities
        await loadBusinessEntities(true)
      } catch (error) {
        showError(
          error,
          'Failed to approve automated motor vehicle application. Please try again.'
        )
        throw error
      } finally {
        setLoading((prev) => prev - 1)
      }
    },
    [registration?.id, getRegistration, loadBusinessEntities, showError]
  )

  // Reject the application, after submission and certification
  const reject = React.useCallback(
    async (restrictionsNote?: string) => {
      if (!registration?.id)
        throw new Error('Cannot reject application without registration')

      try {
        setLoading((prev) => prev + 1)
        // Reject the application
        await AutonomousVehicleAuthorityService.reject(
          registration.id,
          restrictionsNote ? { restrictionsNote } : {}
        )

        // Reload registration to get updated status
        await getRegistration()

        // Refresh auth to get updated business entities
        await loadBusinessEntities(true)
      } catch (error) {
        showError(
          error,
          'Failed to reject automated motor vehicle application. Please try again.'
        )
        throw error
      } finally {
        setLoading((prev) => prev - 1)
      }
    },
    [registration?.id, getRegistration, loadBusinessEntities, showError]
  )

  // Reset the provider
  const reset = React.useCallback(async () => {
    // Reset
    await Promise.all([getRegistration(), listContacts(), loadVehicles()])
  }, [getRegistration, listContacts, loadVehicles])

  // Load registration on mount
  React.useEffect(() => {
    getRegistration()
  }, [getRegistration])

  // Load contacts when registration changes
  React.useEffect(() => {
    listContacts()
  }, [listContacts])

  // Load vehicles when registration changes
  React.useEffect(() => {
    loadVehicles()
  }, [loadVehicles])

  const isLoading = useMemo(() => loading > 0, [loading])
  return (
    <AutonomousVehicleApplicationContext.Provider
      value={{
        loading: isLoading,
        registration,
        primaryContact,
        vehicles,

        register,
        registerFromUsdot,
        setPrimaryContact,
        addVehicle,
        removeVehicle,
        loadVehicles,
        submit,
        approve,
        reject,
        reset,
      }}
    >
      {children}
    </AutonomousVehicleApplicationContext.Provider>
  )
}

export function useAutonomousVehicleApplication() {
  const context = React.useContext(AutonomousVehicleApplicationContext)
  if (context === undefined)
    throw new Error(
      'useAutonomousVehicleApplication must be used within a AutonomousVehicleApplicationProvider'
    )

  return context
}
